import { Download, Loader2, RefreshCw, Sparkles } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import {
  useAnalysesQuery,
  useAnalysisQuery,
  useCreateAnalysisMutation,
  useEnhanceAnalysisMutation,
} from "@/entities/projects/queries";
import type { AnalysisType, ProjectOut } from "@/entities/projects/types";
import { ApiError } from "@/shared/api/http";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { IdeaCard } from "@/widgets/ProjectPage/IdeaCard";

const TYPE_HUMAN: Record<AnalysisType, { title: string; sub: string }> = {
  video: {
    title: "Темы для видео",
    sub: "Что снимать в ближайшее время — из вашей семантики.",
  },
  posts: {
    title: "Темы для постов",
    sub: "О чём писать в блоге / телеграме — с углами раскрытия и форматом.",
  },
  lead_magnets: {
    title: "Лидмагниты",
    sub: "Чек-листы и гайды по болевым запросам — для сбора email-базы.",
  },
  full: {
    title: "Полный отчёт",
    sub: "H1/Title/Description/FAQ для всех тем — для большого SEO-набега.",
  },
};

export interface AnalysisTabProps {
  project: ProjectOut;
  type: AnalysisType;
}

export function AnalysisTab({
  project,
  type,
}: AnalysisTabProps): JSX.Element {
  const analysesQ = useAnalysesQuery(project.id);
  const existing = analysesQ.data?.find((a) => a.type === type);

  if (analysesQ.isLoading) {
    return (
      <div className="glass-card p-8 text-center text-sm text-muted-foreground">
        Загрузка…
      </div>
    );
  }

  if (!existing) {
    return <CreateForm project={project} type={type} />;
  }
  return <ExistingAnalysis analysisId={existing.id} project={project} type={type} />;
}

// ---------- create form (empty state) ----------

function CreateForm({
  project,
  type,
}: {
  project: ProjectOut;
  type: AnalysisType;
}): JSX.Element {
  const [maxItems, setMaxItems] = React.useState(15);
  const [enhance, setEnhance] = React.useState(true);
  const createM = useCreateAnalysisMutation(project.id);

  const projectReady =
    project.status === "done" || project.status === "partial";

  const onCreate = (): void => {
    createM.mutate(
      { type, max_items: maxItems, enhance },
      {
        onSuccess: () => toast.success("Анализ создан"),
        onError: (e) =>
          toast.error(
            e instanceof ApiError ? e.message : "Не удалось создать анализ",
          ),
      },
    );
  };

  const human = TYPE_HUMAN[type];

  return (
    <div className="glass-card mx-auto max-w-xl p-6">
      <h2 className="text-xl font-bold text-white">{human.title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{human.sub}</p>

      <div className="mt-6 space-y-5">
        <div className="space-y-2">
          <div className="flex justify-between">
            <Label>Сколько идей</Label>
            <span className="font-mono text-sm text-cyan">{maxItems}</span>
          </div>
          <input
            type="range"
            min={5}
            max={50}
            step={5}
            value={maxItems}
            onChange={(e) => setMaxItems(Number(e.target.value))}
            className="w-full accent-purple"
          />
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>5</span>
            <span>50</span>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <Switch
            checked={enhance}
            onCheckedChange={setEnhance}
            id="enhance"
            className="mt-1"
          />
          <label htmlFor="enhance" className="cursor-pointer">
            <div className="text-sm font-semibold text-white">
              Усилить через ChatGPT
            </div>
            <div className="text-xs text-muted-foreground">
              ≈ $0.0005 за идею · gpt-4o-mini · 30 секунд работы в фоне
            </div>
          </label>
        </div>

        {!projectReady && (
          <div className="rounded-xl border border-amber/30 bg-amber/[0.07] p-3 text-sm">
            Создавать анализ можно только когда проект собран (status=done/partial).
            Сейчас: <b>{project.status}</b>.
          </div>
        )}

        <Button
          type="button"
          onClick={onCreate}
          disabled={!projectReady || createM.isPending}
          className="w-full bg-gradient-primary text-white shadow-glow-purple disabled:opacity-60"
        >
          {createM.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Создать анализ
        </Button>
      </div>
    </div>
  );
}

// ---------- existing analysis ----------

function ExistingAnalysis({
  analysisId,
  project,
  type,
}: {
  analysisId: string;
  project: ProjectOut;
  type: AnalysisType;
}): JSX.Element {
  const aQ = useAnalysisQuery(analysisId);
  const enhanceM = useEnhanceAnalysisMutation(analysisId);

  if (aQ.isLoading) {
    return (
      <div className="glass-card p-8 text-center text-sm text-muted-foreground">
        Загрузка…
      </div>
    );
  }
  if (aQ.isError || !aQ.data) {
    return (
      <div className="glass-card p-5 text-sm text-pink">
        Не удалось загрузить анализ
      </div>
    );
  }

  const a = aQ.data;
  const items = (a.items_json ?? []) as Parameters<
    typeof IdeaCard
  >[0]["item"][];
  const enhancing = a.status === "enhancing";
  const enhanced = a.status === "enhanced";
  const filledCount = items.filter((i) => i.ideas !== null).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div>
          <div className="text-base font-bold text-white">
            {TYPE_HUMAN[type].title}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {items.length} карточек ·{" "}
            {enhancing
              ? `усиливаем через GPT… ${filledCount} из ${items.length}`
              : enhanced
                ? "обогащены через GPT"
                : a.status === "failed"
                  ? "ошибка обогащения"
                  : "готов без идей"}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="border-white/15 bg-white/[0.04]"
            disabled={enhancing || enhanceM.isPending}
            onClick={() =>
              enhanceM.mutate(undefined, {
                onSuccess: () => toast.success("Обогащение запущено"),
                onError: (e) =>
                  toast.error(
                    e instanceof ApiError
                      ? e.message
                      : "Не удалось перезапустить",
                  ),
              })
            }
          >
            <RefreshCw className="h-4 w-4" /> Регенерировать идеи
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-white/15 bg-white/[0.04]"
            onClick={async () => {
              try {
                const mod = await import("@/entities/projects/api");
                const blob = await mod.exportProjectFile(project.id, {
                  format: "html",
                  analysis_id: analysisId,
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${project.name.replace(/[^a-z0-9]+/gi, "-")}-${type}.html`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch {
                toast.error("Не удалось скачать");
              }
            }}
          >
            <Download className="h-4 w-4" /> HTML
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="glass-card p-5 text-sm text-muted-foreground">
          Нет карточек.
        </div>
      ) : (
        items.map((item) => (
          <IdeaCard key={item.cluster_id} item={item} type={type} />
        ))
      )}
    </div>
  );
}
